import type { Note, NoteStats, CardResult, AdaptiveConfig, DifficultyLevel, Clef } from '../types/music';
import { getNotesByClef, getNotesByDifficulty, getNotesByAccidental, getNotesByMidiRange, getMidiRangeFromStrings } from '../data/notes';

/**
 * Default adaptive configuration values
 */
export const DEFAULT_ADAPTIVE_CONFIG: AdaptiveConfig = {
  // 80% accuracy required to unlock harder notes
  unlockAccuracyThreshold: 0.80,
  // Must respond within 3 seconds on average to unlock harder notes
  unlockSpeedThreshold: 3000,
  // Below 60% accuracy, scale back to easier notes
  scaleBackAccuracyThreshold: 0.60,
  // Use last 20 cards for rolling statistics
  rollingWindowSize: 20,
  // Need at least 10 attempts before adjusting difficulty
  minAttemptsBeforeAdjust: 10
};

/**
 * Calculate accuracy from recent results
 */
export function calculateRollingAccuracy(results: CardResult[]): number {
  if (results.length === 0) return 0;
  const correct = results.filter(r => r.correct).length;
  return correct / results.length;
}

/**
 * Calculate average response time from recent results
 */
export function calculateRollingAverageTime(results: CardResult[]): number {
  if (results.length === 0) return Infinity;
  const correctResults = results.filter(r => r.correct);
  if (correctResults.length === 0) return Infinity;
  const total = correctResults.reduce((sum, r) => sum + r.responseTime, 0);
  return total / correctResults.length;
}

/**
 * Determine if the user should unlock harder notes
 */
export function shouldUnlockHarderNotes(
  results: CardResult[],
  config: AdaptiveConfig = DEFAULT_ADAPTIVE_CONFIG
): boolean {
  if (results.length < config.minAttemptsBeforeAdjust) {
    return false;
  }

  const accuracy = calculateRollingAccuracy(results);
  const avgTime = calculateRollingAverageTime(results);

  return accuracy >= config.unlockAccuracyThreshold &&
         avgTime <= config.unlockSpeedThreshold;
}

/**
 * Determine if the user should scale back to easier notes
 */
export function shouldScaleBackToEasier(
  results: CardResult[],
  config: AdaptiveConfig = DEFAULT_ADAPTIVE_CONFIG
): boolean {
  if (results.length < config.minAttemptsBeforeAdjust) {
    return false;
  }

  const accuracy = calculateRollingAccuracy(results);
  return accuracy < config.scaleBackAccuracyThreshold;
}

/**
 * Get the current difficulty level based on performance
 */
export function getCurrentDifficultyLevel(
  results: CardResult[],
  currentLevel: DifficultyLevel,
  config: AdaptiveConfig = DEFAULT_ADAPTIVE_CONFIG
): DifficultyLevel {
  if (results.length < config.minAttemptsBeforeAdjust) {
    return currentLevel;
  }

  if (shouldUnlockHarderNotes(results, config)) {
    // Upgrade difficulty
    if (currentLevel === 'easy') return 'medium';
    if (currentLevel === 'medium') return 'hard';
    return 'hard';
  }

  if (shouldScaleBackToEasier(results, config)) {
    // Downgrade difficulty
    if (currentLevel === 'hard') return 'medium';
    if (currentLevel === 'medium') return 'easy';
    return 'easy';
  }

  return currentLevel;
}

/**
 * Get the display level name for UI
 */
export function getDifficultyDisplayName(level: DifficultyLevel): string {
  switch (level) {
    case 'easy': return 'Beginner';
    case 'medium': return 'Intermediate';
    case 'hard': return 'Advanced';
  }
}

/**
 * Bass clef progression stages
 *
 * Stage 1 (Easy): C3 to C4 - Main staff notes
 * Stage 2: Add G2, A2, B2 - Lower notes approaching ledger lines
 * Stage 3: Add F2 - First low ledger line
 * Stage 4: Add E2 - Lower ledger line
 *
 * This allows gradual expansion of the note range as the user improves
 */
export interface ProgressionStage {
  level: number;
  lowNote: string;
  highNote: string;
  description: string;
}

export const BASS_PROGRESSION_STAGES: ProgressionStage[] = [
  { level: 1, lowNote: 'C3', highNote: 'C4', description: 'Staff notes (C3-C4)' },
  { level: 2, lowNote: 'G2', highNote: 'C4', description: 'Extended down to G2' },
  { level: 3, lowNote: 'F2', highNote: 'C4', description: 'Added low F2' },
  { level: 4, lowNote: 'E2', highNote: 'D4', description: 'Full range (E2-D4)' },
  { level: 5, lowNote: 'E2', highNote: 'F4', description: 'Full range with high ledgers' }
];

export const TREBLE_PROGRESSION_STAGES: ProgressionStage[] = [
  { level: 1, lowNote: 'C4', highNote: 'G5', description: 'Staff notes (C4-G5)' },
  { level: 2, lowNote: 'B3', highNote: 'A5', description: 'Extended range' },
  { level: 3, lowNote: 'A3', highNote: 'B5', description: 'Added ledger lines' },
  { level: 4, lowNote: 'G3', highNote: 'C6', description: 'Full range' }
];

/**
 * Get the current progression stage based on performance
 */
export function getCurrentProgressionStage(
  clef: Clef,
  results: CardResult[],
  currentStage: number,
  config: AdaptiveConfig = DEFAULT_ADAPTIVE_CONFIG
): number {
  const stages = clef === 'treble' ? TREBLE_PROGRESSION_STAGES : BASS_PROGRESSION_STAGES;
  const maxStage = stages.length;

  if (results.length < config.minAttemptsBeforeAdjust) {
    return currentStage;
  }

  if (shouldUnlockHarderNotes(results, config) && currentStage < maxStage) {
    return currentStage + 1;
  }

  if (shouldScaleBackToEasier(results, config) && currentStage > 1) {
    return currentStage - 1;
  }

  return currentStage;
}

/**
 * Get the note range for a progression stage
 */
export function getProgressionStageRange(
  clef: Clef,
  stage: number
): { low: string; high: string } {
  const stages = clef === 'treble' ? TREBLE_PROGRESSION_STAGES : BASS_PROGRESSION_STAGES;
  const clampedStage = Math.max(1, Math.min(stage, stages.length));
  const stageData = stages[clampedStage - 1];
  return { low: stageData.lowNote, high: stageData.highNote };
}

/**
 * Build the active note deck based on settings and performance
 */
export function buildActiveDeck(
  clef: Clef,
  lowNote: string,
  highNote: string,
  includeNaturals: boolean,
  includeSharps: boolean,
  includeFlats: boolean,
  maxDifficulty: DifficultyLevel = 'hard'
): Note[] {
  // Get all notes for the clef
  let notes = getNotesByClef(clef);

  // Filter by difficulty
  notes = getNotesByDifficulty(notes, maxDifficulty);

  // Filter by accidentals
  notes = getNotesByAccidental(notes, includeNaturals, includeSharps, includeFlats);

  // Filter by MIDI range
  const { lowMidi, highMidi } = getMidiRangeFromStrings(lowNote, highNote);
  notes = getNotesByMidiRange(notes, lowMidi, highMidi);

  return notes;
}

/**
 * Calculate note weight based on stats (higher weight = more likely to be selected)
 * Notes the user struggles with get higher weights
 */
export function calculateNoteWeight(
  noteId: string,
  stats: Map<string, NoteStats>,
  recentNotes: string[],
  baseWeight: number = 1.0
): number {
  let weight = baseWeight;

  const noteStats = stats.get(noteId);

  if (noteStats) {
    // Lower accuracy = higher weight (practice weak notes more)
    const accuracy = noteStats.attempts > 0
      ? noteStats.correct / noteStats.attempts
      : 0.5; // Default to 50% for new notes
    weight *= (2 - accuracy); // Range: 1.0 to 2.0

    // Slower response time = higher weight
    if (noteStats.averageResponseTime > 0) {
      const timeBonus = Math.min(noteStats.averageResponseTime / 3000, 1.5);
      weight *= (1 + timeBonus * 0.3); // Up to 1.45x for slow notes
    }

    // Notes with fewer attempts get a slight boost
    if (noteStats.attempts < 5) {
      weight *= 1.2;
    }
  } else {
    // New notes get a boost to ensure they're introduced
    weight *= 1.3;
  }

  // Penalty for recently shown notes to avoid repetition
  const recentIndex = recentNotes.indexOf(noteId);
  if (recentIndex !== -1) {
    // More recent = bigger penalty
    const recencyPenalty = 1 - ((recentNotes.length - recentIndex) / recentNotes.length) * 0.8;
    weight *= recencyPenalty;
  }

  return Math.max(weight, 0.1); // Minimum weight to ensure all notes can appear
}
